<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppFavicon from '../../primitives/AppFavicon/AppFavicon.vue'
import { sourceDomain } from '../AppSources/source-types'
import type { AgentSource } from '@/types/chat'

const props = withDefaults(
  defineProps<{
    /** What a step read: a search's results, a page it opened, or a knowledge page. */
    sources: readonly AgentSource[]
    /** How many rows show; the rest fold into a "+N more" line. */
    limit?: number
    /** The rows sit inside a search well, so their corners follow its inset. */
    nested?: boolean
  }>(),
  { limit: 3, nested: false },
)

const { t } = useI18n()

const shown = computed(() => props.sources.slice(0, props.limit))
const hidden = computed(() => Math.max(0, props.sources.length - props.limit))

function nameOf(source: AgentSource): string {
  return source.title ?? sourceDomain(source.ref)
}
</script>

<template>
  <ul class="flex min-w-0 flex-col">
    <li v-for="(source, index) in shown" :key="`${index}:${source.ref}`" class="min-w-0">
      <component
        :is="source.kind === 'web' ? 'a' : 'div'"
        v-bind="
          source.kind === 'web'
            ? { href: source.ref, target: '_blank', rel: 'noopener noreferrer' }
            : {}
        "
        :class="nested ? 'rounded-4-in-2' : 'rounded-2'"
        class="flex min-w-0 items-center gap-4 px-4 py-2 text-body-sm text-text-primary outline-none transition-colors duration-fast ease-soft hovered:bg-bg-wash focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid focus-visible:outline-accent-blue motion-safe:animate-fade-in motion-reduce:transition-none"
      >
        <AppFavicon :src="source.favicon" :kind="source.kind" />
        <span class="min-w-0 flex-1 truncate">{{ nameOf(source) }}</span>
        <span
          v-if="source.kind === 'web' && source.title"
          class="max-w-2/5 shrink-0 truncate text-caption text-text-tertiary"
          >{{ sourceDomain(source.ref) }}</span
        >
      </component>
    </li>
    <li v-if="hidden" class="px-4 py-2 text-caption text-text-tertiary">
      {{ t('chat.activity.moreSources', { count: hidden }) }}
    </li>
  </ul>
</template>
